import React, { useMemo } from 'react';
import EChart from '../../components/Charts/Echart';
import { buildProjectCostCurve } from './projectCostCurveData';

const currency = new Intl.NumberFormat('es-AR', {
  style: 'currency', currency: 'ARS', maximumFractionDigits: 2,
});

const shortDateLabel = value => {
  const [, month, day] = String(value).split('-');
  return month && day ? `${day}/${month}` : value;
};

export default function ProjectCostCurve({ tasks = [] }) {
  const curve = useMemo(() => buildProjectCostCurve(tasks), [tasks]);
  const option = useMemo(() => ({
    animationDuration: 280,
    color: ['#73879c', '#1abb9c'],
    grid: { top: 58, right: 28, bottom: 48, left: 88 },
    legend: { top: 4, textStyle: { color: '#52677a', fontSize: 12 } },
    tooltip: {
      trigger: 'axis',
      valueFormatter: value => value == null ? 'Sin dato' : currency.format(value),
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: curve.points.map(point => point.date),
      axisLabel: { color: '#52677a', formatter: shortDateLabel, hideOverlap: true },
      axisLine: { lineStyle: { color: '#ccd6df' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: '#52677a', formatter: value => currency.format(value) },
      splitLine: { lineStyle: { color: '#e8edf1', type: 'dashed' } },
    },
    series: [
      curve.plannedEventCount > 0 && {
        name: 'Costo planificado acumulado', type: 'line', step: 'end', showSymbol: true,
        symbolSize: 7, lineStyle: { width: 3 }, connectNulls: false,
        data: curve.points.map(point => point.planned),
      },
      curve.actualEventCount > 0 && {
        name: 'Costo real acumulado', type: 'line', step: 'end', showSymbol: true,
        symbolSize: 7, lineStyle: { width: 3 }, connectNulls: false,
        data: curve.points.map(point => point.actual),
      },
    ].filter(Boolean),
  }), [curve.actualEventCount, curve.plannedEventCount, curve.points]);

  const hasCurve = curve.plannedEventCount > 0 || curve.actualEventCount > 0;

  return <section className="project-cost-curve" aria-label="Evolución acumulada de costos de tareas">
    <div className="project-cost-curve-heading">
      <div>
        <h3>Evolución de costos de tareas</h3>
        <p>Acumulado planificado según vencimientos y acumulado real según finalizaciones.</p>
      </div>
      <small>Los totales de inversión declarados se muestran por separado.</small>
    </div>
    {hasCurve ? (
      <EChart option={option} className="project-cost-curve-canvas" />
    ) : (
      <div className="project-cost-curve-empty">
        <strong>Todavía no hay costos con fechas suficientes para construir la evolución.</strong>
        <span>Informá costos planificados con vencimiento o costos reales con fecha de finalización.</span>
      </div>
    )}
    {curve.plannedEventCount > 0 && curve.actualEventCount === 0 && (
      <div className="project-cost-curve-note">La evolución real aparecerá cuando se informen costos reales y fechas de finalización en las tareas.</div>
    )}
    {(curve.plannedWithoutDate > 0 || curve.actualWithoutDate > 0) && (
      <div className="project-cost-curve-warning">
        {curve.plannedWithoutDate > 0 && <span>{curve.plannedWithoutDate} costo(s) planificado(s) no pueden ubicarse porque la tarea no tiene vencimiento.</span>}
        {curve.actualWithoutDate > 0 && <span>{curve.actualWithoutDate} costo(s) real(es) no pueden ubicarse porque la tarea no tiene fecha de finalización.</span>}
      </div>
    )}
  </section>;
}
